// src/components/Modal.jsx
import React, { memo, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MODAL_OPEN_EVENT = "exportnook:modal-open";
const MODAL_CLOSE_EVENT = "exportnook:modal-close";

// Tracks how many modals are open so the body scroll lock only releases
// when the last one closes. Module-level: nested or stacked modals are
// allowed.
let openModalCount = 0;

function lockBodyScroll() {
  if (typeof document === "undefined") return;
  openModalCount += 1;
  if (openModalCount === 1) {
    document.body.classList.add("modal-open");
  }
}

function unlockBodyScroll() {
  if (typeof document === "undefined") return;
  openModalCount = Math.max(0, openModalCount - 1);
  if (openModalCount === 0) {
    document.body.classList.remove("modal-open");
  }
}

const Modal = memo(function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  disableClose,
  modalId,
}) {
  const idRef = useRef(
    modalId || `modal_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
  );
  const wasOpenRef = useRef(false);
  const [isMobile, setIsMobile] = useState(getIsMobile);

  // Re-measure viewport on resize / rotation so the modal switches between
  // centered sheet and full-screen as the breakpoint changes.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 640px)");
    const handle = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handle);
    else mq.addListener(handle);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handle);
      else mq.removeListener(handle);
    };
  }, []);

  // Emit open/close events (used by Layout to auto-close the sidebar menu)
  // and manage the body scroll lock.
  useEffect(() => {
    const id = idRef.current;

    if (open && !wasOpenRef.current) {
      wasOpenRef.current = true;
      lockBodyScroll();
      window.dispatchEvent(new CustomEvent(MODAL_OPEN_EVENT, { detail: { id } }));
      return;
    }

    if (!open && wasOpenRef.current) {
      wasOpenRef.current = false;
      unlockBodyScroll();
      window.dispatchEvent(new CustomEvent(MODAL_CLOSE_EVENT, { detail: { id } }));
    }
  }, [open]);

  // If the modal unmounts while open, release the lock and emit a close.
  useEffect(() => {
    const id = idRef.current;
    return () => {
      if (wasOpenRef.current) {
        wasOpenRef.current = false;
        unlockBodyScroll();
        window.dispatchEvent(new CustomEvent(MODAL_CLOSE_EVENT, { detail: { id } }));
      }
    };
  }, []);

  // ESC-to-close.
  useEffect(() => {
    if (!open || disableClose) return undefined;
    const handleKey = (e) => {
      if (e.key === "Escape" && typeof onClose === "function") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, disableClose, onClose]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  const handleBackdropClick = disableClose ? undefined : onClose;
  const handleCloseClick = disableClose ? undefined : onClose;

  const sheetStyle = isMobile ? sheetMobile : sheetDesktop;
  const content = (
    <div
      style={backdrop}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === "string" ? title : undefined}
    >
      <div style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div style={{ fontWeight: 700 }}>{title}</div>
          {!disableClose && (
            <button onClick={handleCloseClick} style={xBtn} aria-label="Close">
              ✕
            </button>
          )}
        </div>
        <div style={body}>{children}</div>
        {footer && <div style={footerBox}>{footer}</div>}
      </div>
    </div>
  );

  return createPortal(content, document.body);
});

export default Modal;

// Mobile-aware sizing: on phones (<= 640px) the sheet goes full-screen so
// forms are usable without zooming; on larger viewports it's a centered
// rounded dialog capped at 900px / 92vw.
const backdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: "0",
};

function getIsMobile() {
  return (
    typeof window !== "undefined" &&
    !!window.matchMedia &&
    window.matchMedia("(max-width: 640px)").matches
  );
}

const sheetMobile = {
  width: "100vw",
  height: "100vh",
  maxHeight: "100vh",
  background: "#fff",
  borderRadius: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  boxShadow: "none",
  paddingTop: "env(safe-area-inset-top, 0px)",
  paddingBottom: "env(safe-area-inset-bottom, 0px)",
};

const sheetDesktop = {
  width: "min(900px, 92vw)",
  maxWidth: "100vw",
  maxHeight: "92vh",
  background: "#fff",
  borderRadius: 14,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
};

const header = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 12px",
  borderBottom: "1px solid #e5e7eb",
  flexShrink: 0,
};

const body = {
  padding: 12,
  overflowY: "auto",
  overflowX: "hidden",
  flex: 1,
  minHeight: 0,
  minWidth: 0,
};

const xBtn = {
  border: "none",
  background: "transparent",
  fontSize: 18,
  cursor: "pointer",
  color: "#475569",
  padding: "4px 8px",
};

const footerBox = {
  padding: 12,
  borderTop: "1px solid #e5e7eb",
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  flexShrink: 0,
};
